/**
 * Typed registry of Firebase Remote Config feature-flag keys.
 *
 * Read flags through these named entries, never raw string literals, so the
 * naming convention and the in-repo defaults stay in one place.
 *
 * Naming rule: lowercase snake_case, starts with a letter, ends with
 * `_enabled`. Audience-gated flags use the JSON value shape
 * `{ "enabled": boolean, "allowlist"?: string[] }` parsed by `parseAudienceFlag`.
 */
export const FEATURE_FLAGS = {
  /**
   * Rollout flag for the manual payroll flow on the Lohnbuchhaltung
   * company-month page. Audience-gated by Firebase UID so specific operators
   * see `Abrechnung durchführen` before broader rollout.
   */
  PAYROLL_AUTOMATION_ENABLED: 'payroll_automation_enabled',

  /**
   * Rollout gate for the CL-1420 Chatwoot customer chat (and the operator-side
   * Chatwoot inbox), replacing Intercom. Company-ID audience-gated: the JSON
   * `allowlist` holds company UIDs, so the pilot can be scoped to specific
   * companies. Evaluate per-company (the customer bubble) with
   * `useChatwootEnabledForCompany`, or app-wide for the signed-in user (does the
   * user belong to ANY allowlisted company?) with `useChatwootEnabledForUser`.
   */
  CHATWOOT_COMPANIES_ENABLED: 'chatwoot_companies_enabled',

  /** Bescheinigungen rollout gate, company-UID audience-gated like Chatwoot. */
  BESCHEINIGUNGEN_ENABLED: 'bescheinigungen_enabled',

  /** Absence re-validation in Datenbereinigung, company-UID audience-gated. */
  ABSENCE_VALIDATION_ENABLED: 'absence_validation_enabled',

  /**
   * Employee leave requests and vacation balance, company-UID audience-gated.
   * Switched per company with `setCompanyAudienceFlag.mjs --flag leave-requests`.
   */
  LEAVE_REQUESTS_ENABLED: 'leave_requests_enabled',

  /**
   * Vacation year-end carry-over, valid-from contract history and the exit
   * flow, company-UID audience-gated. Builds on leave requests, so use
   * `useVacationYearActive`. Switched per company with
   * `setCompanyAudienceFlag.mjs --flag vacation-year`.
   */
  VACATION_YEAR_ENABLED: 'vacation_year_enabled',

  /**
   * Employees report illness and child sickness, employers complete the
   * reports, company-UID audience-gated. Builds on leave requests, so use
   * `useAbsenceReportsActive`. Switched per company with
   * `setCompanyAudienceFlag.mjs --flag absence-reports`.
   */
  ABSENCE_REPORTS_ENABLED: 'absence_reports_enabled',

  COMPANY_ONBOARDING_ENABLED: 'company_onboarding_enabled',

  /**
   * Plausibility warnings for variable payments (minimum wage, hours, surcharge
   * factors, duplicates), company-UID audience-gated.
   */
  VARIABLE_PAYMENT_VALIDATION_ENABLED: 'variable_payments_validation_enabled',

  /**
   * Kill-switch for the employee setup wizard autosave: per-step server saves
   * and the local draft buffer. Disabled → today's behavior, a single save at
   * the final wizard step and no draft buffer activity.
   *
   * Defaults to on in `REMOTE_CONFIG_DEFAULTS` (see `DEFAULT_ON_FLAGS`), so the
   * Console parameter is what holds it back: staging publishes `true`,
   * production publishes `false` until rollout. `useFeatureFlag` reads off until
   * Remote Config activates, so the published value always wins.
   */
  EMPLOYEE_SETUP_AUTOSAVE_ENABLED: 'employee_setup_autosave_enabled',

  /**
   * Sends the help button to the in-app help center (`/help`) instead of the
   * external help site. The `/help` page itself is always reachable, so the
   * articles can be checked there before the button is switched over.
   */
  HELP_CENTER_IN_APP_ENABLED: 'help_center_in_app_enabled',

  /**
   * Pre-fills the hourly wage from time tracking, company-UID audience-gated.
   * Switched per company with `setCompanyAudienceFlag.mjs --flag hours-prefill`.
   * DATEV LODAS companies and companies without time tracking get no pre-fill
   * even when allowlisted, the server decides that.
   */
  HOURS_PREFILL_ENABLED: 'hours_prefill_enabled',

  /**
   * The monthly hours balance on the timesheets in place of the 52/12
   * statistic, company-UID audience-gated and independent of the other flags.
   * Switched per company with `setCompanyAudienceFlag.mjs --flag hours-balance`.
   */
  HOURS_BALANCE_ENABLED: 'hours_balance_enabled',
} as const

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS]

/**
 * Flags whose in-repo default is `"true"`, i.e. the feature is the intended
 * behaviour and Remote Config carries an explicit `false` to hold it back.
 *
 * Use this only for a flag that exists as a kill-switch rather than as a
 * rollout gate, and only once the Console parameter holding it off is already
 * published in every environment that must stay dark. `useFeatureFlag` reads a
 * flag as off until Remote Config activates, so a missed publish fails closed
 * rather than shipping the feature on the next deploy.
 */
export const DEFAULT_ON_FLAGS: readonly FeatureFlagKey[] = [
  FEATURE_FLAGS.EMPLOYEE_SETUP_AUTOSAVE_ENABLED,
]

/**
 * In-repo Remote Config defaults. Flags default to `"false"` here and in the
 * Firebase Console, and the two maps must agree — except for the kill-switches
 * listed in `DEFAULT_ON_FLAGS`, which default to `"true"` and rely on a
 * published Console `false` to stay off. Wired into Remote Config at init in
 * `firebaseConfig.js`.
 */
export const REMOTE_CONFIG_DEFAULTS: Record<FeatureFlagKey, string> = {
  [FEATURE_FLAGS.PAYROLL_AUTOMATION_ENABLED]: 'false',
  [FEATURE_FLAGS.CHATWOOT_COMPANIES_ENABLED]: 'false',
  [FEATURE_FLAGS.BESCHEINIGUNGEN_ENABLED]: 'false',
  [FEATURE_FLAGS.ABSENCE_VALIDATION_ENABLED]: 'false',
  [FEATURE_FLAGS.LEAVE_REQUESTS_ENABLED]: 'false',
  [FEATURE_FLAGS.VACATION_YEAR_ENABLED]: 'false',
  [FEATURE_FLAGS.ABSENCE_REPORTS_ENABLED]: 'false',
  [FEATURE_FLAGS.COMPANY_ONBOARDING_ENABLED]: 'false',
  [FEATURE_FLAGS.VARIABLE_PAYMENT_VALIDATION_ENABLED]: 'false',
  [FEATURE_FLAGS.EMPLOYEE_SETUP_AUTOSAVE_ENABLED]: 'true',
  [FEATURE_FLAGS.HELP_CENTER_IN_APP_ENABLED]: 'false',
  [FEATURE_FLAGS.HOURS_PREFILL_ENABLED]: 'false',
  [FEATURE_FLAGS.HOURS_BALANCE_ENABLED]: 'false',
}
